'use client';

import { useState } from 'react';
import { IntegrationCard } from '@/components/marketing/integrations/IntegrationCard';
import { byStatus } from '@/components/marketing/integrations/status';
import s from '@/components/marketing/page/page.module.css';
import type {
  IntegrationCategory,
  IntegrationCategoryId,
  IntegrationEntry,
} from '@/lib/brand/integrations-catalog';
import { cn } from '@/lib/utils';

interface CatalogFilterProps {
  categories: (IntegrationCategory & { items: IntegrationEntry[] })[];
}

/** Category filter over the full catalog. Results animate in as the
    selection changes. */
export function CatalogFilter({ categories }: CatalogFilterProps) {
  const [active, setActive] = useState<IntegrationCategoryId | 'all'>('all');
  const total = categories.reduce(
    (sum, category) => sum + category.items.length,
    0
  );
  const visible =
    active === 'all'
      ? categories.flatMap(category => category.items).sort(byStatus)
      : (categories.find(category => category.id === active)?.items ?? [])
          .slice()
          .sort(byStatus);

  const options = [
    { id: 'all' as const, name: 'All', count: total },
    ...categories.map(category => ({
      id: category.id,
      name: category.name.replace('&', 'and'),
      count: category.items.length,
    })),
  ];

  return (
    <>
      <div
        role="group"
        aria-label="Filter by category"
        className="-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-2 [scrollbar-width:none] md:mx-0 md:flex-wrap md:justify-center md:px-0"
      >
        {options.map(option => (
          <button
            key={option.id}
            type="button"
            aria-pressed={active === option.id}
            onClick={() => setActive(option.id)}
            className={cn(
              s.segment,
              'focus-visible:ring-accent-50 shrink-0 bg-white px-4 py-2 text-sm font-medium whitespace-nowrap text-gray-600 shadow-[0_0_0_1px_rgb(15_23_42/0.08)] outline-hidden hover:text-gray-950 focus-visible:ring-2'
            )}
          >
            {option.name}
            <span className="ml-1.5 text-xs tabular-nums opacity-60">
              {option.count}
            </span>
          </button>
        ))}
      </div>
      <p className="sr-only" aria-live="polite">
        {visible.length} integrations shown
      </p>
      <div
        key={active}
        className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3"
        data-spotlight-group
      >
        {visible.map((integration, index) => (
          <IntegrationCard
            key={integration.id}
            integration={integration}
            index={Math.min(index, 10)}
            animate
          />
        ))}
      </div>
    </>
  );
}
